import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { t, Trans } from '@lingui/macro';
import { Button, Popover, SearchInput, P } from '@decisiv/ui-components';
import InputField from '@decisiv/ui-components/lib/atoms/InputField';
import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';
import ChevronRight from '@decisiv/iconix/lib/components/ChevronRight';
import PlusCircleF from '@decisiv/iconix/lib/components/PlusCircleF';

import { filterTree, findNode } from '../../../utils/payloadSchemaTree';
import {
  Empty,
  Example,
  ExpandButton,
  ExpandSpacer,
  FieldBody,
  FieldInput,
  Panel,
  PanelHeader,
  PathButton,
  Required,
  SourceChip,
  SourceRow,
  TreeList,
  TreeRow,
  TriggerSlot,
  TypeTag,
} from './style';

const nodeShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  required: PropTypes.bool,
  example: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  // eslint-disable-next-line react/forbid-prop-types
  children: PropTypes.array,
});

export const sourceShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  nodes: PropTypes.arrayOf(nodeShape).isRequired,
});

const MIN_PANEL_WIDTH = 320;

// The attribute text field with a trigger inside it, as the home search does.
// The popover hangs from the input box, shows the trigger payload as a tree
// and highlights the node matching the typed value when one exists.
export default function AttributePicker({
  sources,
  onSelect,
  value = '',
  disabled = false,
  ...inputProps
}) {
  const inputContainerRef = useRef(null);
  const popoverRef = useRef(null);
  const [sourceId, setSourceId] = useState(sources[0]?.id);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [selected, setSelected] = useState(null);
  const [panelWidth, setPanelWidth] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  const source = sources.find((item) => item.id === sourceId) || sources[0];
  const nodes = source ? filterTree(source.nodes, query) : [];
  const searching = query.trim().length > 0;

  const syncWithValue = () => {
    setQuery('');
    setPanelWidth(inputContainerRef.current?.offsetWidth || null);

    const match = sources
      .map((item) => ({ item, found: findNode(item.nodes, value) }))
      .find(({ found }) => found);

    if (!match) {
      setSelected(null);
      return;
    }
    setSourceId(match.item.id);
    setExpanded(new Set(match.found.ancestors));
    setSelected(value);
  };

  const toggle = (path) =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  const pick = (path) => {
    onSelect(path);
    popoverRef.current?.hide();
  };

  const renderNode = (node, depth) => {
    const hasChildren = node.children.length > 0;
    const open = searching || expanded.has(node.path);
    const isSelected = node.path === selected;

    return (
      <React.Fragment key={node.path}>
        <TreeRow
          style={{ paddingLeft: `${depth * 1.5}rem` }}
          data-selected={isSelected}
        >
          {hasChildren ? (
            <ExpandButton
              type="button"
              aria-label={
                open ? t`Collapse ${node.path}` : t`Expand ${node.path}`
              }
              aria-expanded={open}
              onClick={() => toggle(node.path)}
            >
              {open ? (
                <ChevronDown size="small" />
              ) : (
                <ChevronRight size="small" />
              )}
            </ExpandButton>
          ) : (
            <ExpandSpacer />
          )}
          <PathButton
            type="button"
            title={node.path}
            aria-current={isSelected ? 'true' : undefined}
            onClick={() => pick(node.path)}
          >
            {node.key}
          </PathButton>
          {node.required && (
            <Required title={t`required`} aria-hidden>
              *
            </Required>
          )}
          <TypeTag>{node.type}</TypeTag>
          {node.example !== undefined && (
            <Example title={String(node.example)}>
              {JSON.stringify(node.example)}
            </Example>
          )}
        </TreeRow>
        {hasChildren &&
          open &&
          node.children.map((child) => renderNode(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <>
      <InputField
        {...inputProps}
        value={value}
        disabled={disabled}
        inputContainerRef={inputContainerRef}
      >
        {(fieldProps) => (
          <FieldBody>
            <FieldInput {...fieldProps} />
            <TriggerSlot onClick={(event) => event.stopPropagation()}>
              <Button
                type="button"
                variant="ghost"
                size="small"
                icon={PlusCircleF}
                aria-label={t`Pick attribute from the input payload`}
                aria-expanded={panelOpen}
                aria-haspopup="dialog"
                disabled={disabled}
                onClick={() => popoverRef.current?.toggle()}
              />
            </TriggerSlot>
          </FieldBody>
        )}
      </InputField>
      <Popover
        ref={popoverRef}
        target={inputContainerRef}
        manageEvents={false}
        placement="bottom-start"
        showArrow={false}
        aria-label={t`Input payload attributes`}
        onShow={() => {
          setPanelOpen(true);
          syncWithValue();
        }}
        onHide={() => setPanelOpen(false)}
      >
        <Panel
          style={{
            width: panelWidth
              ? Math.max(panelWidth, MIN_PANEL_WIDTH)
              : undefined,
          }}
        >
          <PanelHeader>
            <P weight="semibold" size="small">
              <Trans>Input payload</Trans>
            </P>
            {sources.length > 1 && (
              <SourceRow>
                {sources.map((item) => (
                  <SourceChip
                    key={item.id}
                    type="button"
                    aria-pressed={item.id === source.id}
                    onClick={() => setSourceId(item.id)}
                  >
                    {item.label}
                  </SourceChip>
                ))}
              </SourceRow>
            )}
            <SearchInput
              size="small"
              searchOn="change"
              debounceTimeout={0}
              aria-label={t`Search attributes`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </PanelHeader>
          <TreeList>
            {nodes.length === 0 ? (
              <Empty>
                {searching
                  ? t`No attribute matches “${query}”`
                  : t`No attributes in this payload`}
              </Empty>
            ) : (
              nodes.map((node) => renderNode(node, 0))
            )}
          </TreeList>
        </Panel>
      </Popover>
    </>
  );
}

AttributePicker.propTypes = {
  sources: PropTypes.arrayOf(sourceShape).isRequired,
  onSelect: PropTypes.func.isRequired,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};
